import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, CheckCircle, Play, Pause } from 'lucide-react';

const InteractiveSteps = ({ 
  title, 
  subtitle, 
  steps, 
  theme = 'blue', 
  autoPlay = false,
  autoPlayInterval = 3000 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);

  // Theme colors
  const themes = {
    blue: {
      primary: 'from-blue-500 to-emerald-500',
      secondary: 'from-blue-50 to-emerald-50',
      accent: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      dot: 'bg-blue-500'
    },
    emerald: {
      primary: 'from-emerald-500 to-blue-500',
      secondary: 'from-emerald-50 to-blue-50',
      accent: 'text-emerald-600',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      dot: 'bg-emerald-500'
    }
  };

  const currentTheme = themes[theme];

  // Auto-play functionality
  React.useEffect(() => {
    if (isPlaying && autoPlay) {
      const interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % steps.length);
      }, autoPlayInterval);
      return () => clearInterval(interval);
    }
  }, [isPlaying, autoPlay, autoPlayInterval, steps.length]);

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const goToStep = (stepIndex) => {
    setActiveStep(stepIndex);
    setIsPlaying(false);
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">{title}</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
          {subtitle}
        </p>
        
        {/* Auto-play controls */}
        {autoPlay && (
          <div className="flex items-center justify-center gap-4 mb-8">
            <button
              onClick={togglePlayPause}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isPlaying 
                  ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              <span className="text-sm font-medium">
                {isPlaying ? 'Pause' : 'Play'} Auto-tour
              </span>
            </button>
            
            {/* Progress dots */}
            <div className="flex items-center gap-2">
              {steps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToStep(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === activeStep 
                      ? `${currentTheme.dot} scale-125` 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Desktop Interactive Layout */}
      <div className="hidden lg:block">
        <div className="grid grid-cols-12 gap-6 items-start">
          {/* Steps Navigation */}
          <div className="col-span-3">
            <div className="space-y-3">
              {steps.map((step, index) => (
                <motion.button
                  key={index}
                  onClick={() => goToStep(index)}
                  className={`w-full text-left p-4 rounded-xl transition-all duration-300 ${
                    index === activeStep
                      ? `${currentTheme.bg} border-2 ${currentTheme.border} shadow-md`
                      : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      index === activeStep 
                        ? `bg-gradient-to-br ${currentTheme.primary} text-white` 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {index < activeStep ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <span className="text-sm font-bold">{index + 1}</span>
                      )}
                    </div>
                    <div>
                      <h3 className={`text-sm font-semibold ${
                        index === activeStep ? currentTheme.accent : 'text-gray-900'
                      }`}>
                        {step.title}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Step {index + 1} of {steps.length}
                      </p>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Active Step Content */}
          <div className="col-span-9">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8"
              >
                <div className="flex items-start gap-6">
                  {/* Step Icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${currentTheme.secondary} flex items-center justify-center flex-shrink-0`}>
                    {React.createElement(steps[activeStep].icon, { className: `w-8 h-8 ${currentTheme.accent}` })}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${currentTheme.primary} flex items-center justify-center`}>
                        <span className="text-lg font-bold text-white">{activeStep + 1}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        {steps[activeStep].title}
                      </h3>
                    </div>
                    
                    <p className="text-gray-600 text-lg leading-relaxed mb-6">
                      {steps[activeStep].description}
                    </p>

                    {/* Additional Info */}
                    {steps[activeStep].details && (
                      <div className="bg-gray-50 rounded-xl p-4 mb-6">
                        <h4 className="font-semibold text-gray-900 mb-2">What to expect:</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {steps[activeStep].details.map((detail, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <div className={`w-1.5 h-1.5 rounded-full ${currentTheme.dot}`} />
                              {detail}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Timeline Progress */}
                    <div className="flex items-center gap-4">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <motion.div
                          className={`h-2 rounded-full bg-gradient-to-r ${currentTheme.primary}`}
                          initial={{ width: 0 }}
                          animate={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
                          transition={{ duration: 0.5 }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-600">
                        {activeStep + 1} / {steps.length}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Interactive Layout */}
      <div className="lg:hidden">
        {/* Mobile Steps Navigation */}
        <div className="flex overflow-x-auto gap-3 mb-8 pb-4">
          {steps.map((step, index) => (
            <button
              key={index}
              onClick={() => goToStep(index)}
              className={`flex-shrink-0 px-4 py-2 rounded-full transition-all duration-300 ${
                index === activeStep
                  ? `bg-gradient-to-r ${currentTheme.primary} text-white`
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              <span className="text-sm font-medium">
                {index < activeStep ? '✓' : index + 1} {step.title}
              </span>
            </button>
          ))}
        </div>

        {/* Mobile Active Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6"
          >
            <div className="text-center mb-6">
              <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${currentTheme.primary} flex items-center justify-center mx-auto mb-4`}>
                <span className="text-2xl font-bold text-white">{activeStep + 1}</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {steps[activeStep].title}
              </h3>
            </div>
            
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${currentTheme.secondary} flex items-center justify-center mx-auto mb-4`}>
              {React.createElement(steps[activeStep].icon, { className: `w-6 h-6 ${currentTheme.accent}` })}
            </div>
            
            <p className="text-gray-600 text-center leading-relaxed mb-6">
              {steps[activeStep].description}
            </p>

            {/* Additional Info */}
            {steps[activeStep].details && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2 text-center">What to expect:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {steps[activeStep].details.map((detail, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${currentTheme.dot}`} />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Navigation Controls */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => goToStep(Math.max(0, activeStep - 1))}
                disabled={activeStep === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeStep === 0 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                <span className="text-sm font-medium">Previous</span>
              </button>

              <div className="flex items-center gap-2">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToStep(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === activeStep 
                        ? `${currentTheme.dot} scale-125` 
                        : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={() => goToStep(Math.min(steps.length - 1, activeStep + 1))}
                disabled={activeStep === steps.length - 1}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeStep === steps.length - 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : `bg-gradient-to-r ${currentTheme.primary} text-white hover:opacity-90`
                }`}
              >
                <span className="text-sm font-medium">Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Completion Message */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="mt-12 text-center"
      >
        <div className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${currentTheme.secondary} rounded-full border ${currentTheme.border}`}>
          <div className={`w-2 h-2 rounded-full ${currentTheme.dot} animate-pulse`} />
          <span className="text-sm font-medium text-gray-700">
            Complete all steps to get started with TripSecure
          </span>
        </div>
      </motion.div>
    </div>
  );
};

export default InteractiveSteps;
